import React, { FC, useState } from 'react';
import {
  BoxV2 as Box,
  ButtonV2 as Button,
  Flex,
  RoleGuard,
} from 'portal-commons';
import {
  Modal,
  ModalType,
  User,
  UserCreatePayload,
  UserUpdatePayload,
} from '../types';
import { faUserCircle } from '@fortawesome/pro-regular-svg-icons';
import SectionCard from './SectionCard';
import { CspProfile } from '@/types/user';
import UserTable from './UserTable';
import { toastFlashMessage } from '@/utils';
import { loginsManagerController, userController } from '@/apis/core';
import UserCreateModal from './UserCreateModal';
import UserEditModal from './UserEditModal';
import UserDeleteModal from './UserDeleteModal';
import { useAuthContext } from '@/contexts';

interface TabUserManagementProps {
  profile: CspProfile;
  users: User[];
  roles: string[];
  secondaryRoles: string[];
  onUsersUpdate: (users: User[]) => void;
}

const TabUserManagement: FC<TabUserManagementProps> = ({
  profile,
  users,
  roles,
  secondaryRoles,
  onUsersUpdate,
}) => {
  const { user, setUser } = useAuthContext();
  const maxPortalUsers = profile.maxPortalUsers || 10;
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<ModalType | undefined>();
  const [targetUser, setTargetUser] = useState<User | undefined>();
  const isCreateDisabled = maxPortalUsers
    ? users.length >= maxPortalUsers
    : false;

  const getUsers = async (delay = false, initialLoad = false) => {
    // TCR-12401: Workaround for Okta update user delay
    delay &&
      (await new Promise((resolve) => {
        setTimeout(resolve, 2000);
      }));

    const response = await loginsManagerController.getUsers();
    response.length > 0 && !initialLoad && onUsersUpdate(response);
    return response || [];
  };

  const handleAddUser = async (data: UserCreatePayload) => {
    const response = await loginsManagerController.createUser(data);
    if (response) {
      toastFlashMessage(
        `New User ${data.firstName} ${data.lastName} Created`,
        'success'
      );
    }
    return !!response;
  };

  const handleUpdateUser = async (data: UserUpdatePayload) => {
    if (!targetUser) return false;

    const response = await loginsManagerController.updateUser(
      targetUser.id,
      data
    );
    if (response) {
      toastFlashMessage(
        `User ${targetUser.profile.fullName} Edit Successful`,
        'success'
      );

      if (targetUser.id === user?.id) {
        const newUser = await userController.getMe();
        if (newUser) {
          setUser(newUser);
        }
      }
    }
    return !!response;
  };

  const handleDeleteUser = async (userToDelete: User) => {
    if (!userToDelete) return false;

    const response = await loginsManagerController.deleteUser(userToDelete.id);
    if (response) {
      toastFlashMessage(
        `User ${userToDelete.profile.fullName} Deleted`,
        'success'
      );
    }
    return !!response;
  };

  const handleModalSubmit = async (
    mode: ModalType,
    data: UserCreatePayload | UserUpdatePayload | User
  ) => {
    setLoading(true);
    let success = false;
    const isEdit = mode === Modal.EDIT;

    try {
      switch (mode) {
        case Modal.ADD:
          success = await handleAddUser(data as UserCreatePayload);
          break;
        case Modal.EDIT:
          success = await handleUpdateUser(data as UserUpdatePayload);
          break;
        case Modal.DELETE:
          success = await handleDeleteUser(data as User);
          break;
        default:
          return;
      }

      if (success) {
        setModal(undefined);
        await getUsers(isEdit);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <SectionCard
      icon={faUserCircle}
      title={
        <Flex sx={{ fontWeight: 600, fontSize: 14, color: '#4D5659', gap: 12 }}>
          <Box sx={{ fontSize: 18 }}>User Management</Box>
          <Box>
            {users.length}/{maxPortalUsers} Portal Users
          </Box>
        </Flex>
      }
      customButton={
        <RoleGuard feature="profile.addUser">
          <Button
            shape="square"
            color="secondary"
            disabled={isCreateDisabled}
            onClick={() => setModal(Modal.ADD)}
            sx={{ borderRadius: 4 }}
          >
            Add User
          </Button>
        </RoleGuard>
      }
      dataTestId="tabUserManagementContainer"
    >
      <UserTable
        loading={loading}
        users={users}
        onEdit={(user) => {
          setTargetUser(user);
          setModal(Modal.EDIT);
        }}
        onDelete={(user) => {
          setTargetUser(user);
          setModal(Modal.DELETE);
        }}
      />
      {isCreateDisabled && (
        <Box
          sx={{
            mt: 'l',
            textAlign: 'center',
            fontSize: 's',
            fontWeight: 400,
          }}
          data-testid="maxUserLimitWarning"
        >
          Maximum number of users reached!
          <br />
          Please delete an existing user if you wish to create a new one.
        </Box>
      )}
      <UserCreateModal
        open={modal === Modal.ADD}
        disabled={loading}
        roles={roles}
        onClose={() => setModal(undefined)}
        onSubmit={(data: UserCreatePayload) => {
          handleModalSubmit(Modal.ADD, data);
        }}
      />
      {targetUser && (
        <>
          <UserEditModal
            open={modal === Modal.EDIT}
            disabled={loading}
            user={targetUser}
            roles={roles}
            secondaryRoles={secondaryRoles}
            onClose={() => setModal(undefined)}
            onSubmit={(data: UserUpdatePayload) => {
              handleModalSubmit(Modal.EDIT, data);
            }}
          />
          <UserDeleteModal
            open={modal === Modal.DELETE}
            disabled={loading}
            user={targetUser}
            onClose={() => setModal(undefined)}
            onSubmit={() => handleModalSubmit(Modal.DELETE, targetUser)}
          />
        </>
      )}
    </SectionCard>
  );
};

export default TabUserManagement;
